// src/pdf/PrintReceiptsV2.js
import PDF from './pdf'
import { formatAccounting, capitalizeFirstLetter } from '../lib/utils'

export default class PrintReceiptsV2 extends PDF {
  /**
   * @param {import('jspdf').DocumentProperties} args
   */
  constructor(args = {}) {
    super()
    this.doc.setProperties({
      unit: 'mm',
      format: 'a4',
      ...args,
    })
    this.addFonts()
  }

  // --- helpers ---------------------------------------------------------------

  /**
   * Format a timestamp to `YYYY-MM-DD HH:mm:ss`
   * Accepts strings or Date-like
   */
  _fmtTs(ts) {
    if (!ts) return ''
    const d = ts instanceof Date ? ts : new Date(ts)
    if (isNaN(d.getTime())) return String(ts) || ''
    const pad = n => String(n).padStart(2, '0')
    const yyyy = d.getFullYear()
    const mm = pad(d.getMonth() + 1)
    const dd = pad(d.getDate())
    const HH = pad(d.getHours())
    const MM = pad(d.getMinutes())
    const SS = pad(d.getSeconds())
    return `${yyyy}-${mm}-${dd} ${HH}:${MM}:${SS}`
  }

  /**
   * Safely read row timestamps from various backend keys
   */
  _rowTimestamps(row) {
    const created =
      row.create_timestamp ??
      row.created_timestamp ??
      row.created_at ??
      row.createdAt ??
      null

    const updated =
      row.update_timestamp ??
      row.updated_timestamp ??
      row.updated_at ??
      row.updatedAt ??
      null

    return {
      created: this._fmtTs(created),
      updated: this._fmtTs(updated),
    }
  }

  // --- header + meta ---------------------------------------------------------

  /**
   * Header: Store + Title + Date Range line + subtle rule + Generated timestamp
   * @param {{ storeName?: string, dateRange?: string, generatedAt?: string|Date }} param
   */
  __header({ storeName = '', dateRange = '', generatedAt = new Date() }) {
    const title = `${capitalizeFirstLetter(storeName)} • Receipt Summary`
    this.centerText({ fontSize: 20, fontStyle: 'bold', text: title, yOffset: 16, color: [33, 33, 33] })

    if (dateRange) {
      this.centerText({ fontSize: 11, text: dateRange, yOffset: 22, color: [84, 84, 84] })
    }

    // "Generated" line (under dateRange)
    const genText = `Generated: ${this._fmtTs(generatedAt)}`
    this.centerText({ fontSize: 9, text: genText, yOffset: 26, color: [120, 120, 120] })

    this.doc.setDrawColor(200, 200, 200)
    this.doc.line(15, 29, 195, 29)
  }

  /**
   * KPI block below header (left column)
   * @param {HeaderData} headerData
   */

  __meta(headerData) {
    const x = 15
    let y = 39
    const delimiter = ':'
    const brandTypeNames = Object.keys(headerData.brandTypes).map(key => headerData.brandTypes[key].name)
    const labels = ['Total Items', 'Total Amount', 'Total Discount', 'Generated By'].concat(brandTypeNames)
    const longest = labels.sort((a, b) => b.length - a.length)[0]
    const textLength = this.getTextWidth(longest + ': ', 12)

    // Section title
    this.generateText({
      text: 'Summary',
      font: 'calibri',
      fontStyle: 'bold',
      fontSize: 12,
      color: [55, 71, 79],
      coordinates: [x, y],
    })
    y += 6

    this.generateField({ label: 'Total Items', value: `${headerData.count}`, coordinates: [x, y], textLength, delimiter })
    y += 5
    this.generateField({
      label: 'Total Amount',
      value: typeof headerData.totalAmount === 'number' ? formatAccounting(headerData.totalAmount) : `${headerData.totalAmount}`,
      coordinates: [x, y],
      textLength,
      delimiter,
    })
    y += 5
    for (const brandTypeId in headerData.brandTypes) {
      const brandType = headerData.brandTypes[brandTypeId]
      this.generateField({
        label: brandType.name,
        value: typeof brandType.amount === 'number' ? formatAccounting(brandType.amount) : `${brandType.amount}`,
        coordinates: [x, y],
        textLength,
        delimiter,
      })
      y += 5
    }
    this.generateField({
      label: 'Total Discount',
      value: typeof headerData.totalDiscount === 'number' ? formatAccounting(headerData.totalDiscount) : `${headerData.totalDiscount}`,
      coordinates: [x, y],
      textLength,
      delimiter,
    })
    y += 5

    if (headerData?.generatedBy) {
      this.generateField({ label: 'Generated By', value: headerData.generatedBy, coordinates: [x, y], textLength, delimiter })
      y += 5
    }

    // horizontal rule to close the meta block
    this.doc.setDrawColor(235, 235, 235)
    this.doc.line(15, y + 3, 195, y + 3)
    this._tableStartY = y + 9
  }

  // --- table -----------------------------------------------------------------

  /**
   * Main table (fits A4 width; includes Created & Updated)
   * @param {Row[]} items
   */
  __table(items) {
    this.doc.autoTable({
      head: [['#', 'STC', 'Qty', 'Amnt', 'Date', 'Entry']],
      body: items,
      startY: Math.max(this._tableStartY || 42, 42),
      theme: 'grid',
      styles: {
        font: 'calibri',
        fontSize: 8,                // tighter to fit more
        halign: 'left',
        cellPadding: 1.5,           // tighter rows
        lineColor: [235, 235, 235],
        overflow: 'linebreak',      // wrap long text
      },
      headStyles: {
        fontStyle: 'bold',
        halign: 'left',
        fillColor: [248, 248, 248],
        textColor: [33, 33, 33],
        lineColor: [200, 200, 200],
      },
      columnStyles: {
        0: { cellWidth: 28 },                  // Receipt #
        2: { halign: 'right', cellWidth: 14 }, // Qty
        3: { halign: 'right', cellWidth: 24 }, // Amount
        4: { cellWidth: 26, halign: 'center' },                  // Date Sold
        5: { halign: 'center' },                  // Created
      },
      footStyles: {
        fillColor: [250, 250, 250],
        textColor: [33, 33, 33],
        fontStyle: 'bold',
        lineColor: [200, 200, 200],
      },
      margin: { top: 29, right: 15, bottom: 20, left: 15 },
      tableWidth: 180,               // enforce width to avoid overflow
      pageBreak: 'auto',
      rowPageBreak: 'auto',
    })
  }

  // --- footer ----------------------------------------------------------------

  __pageFooter() {
    const pageCount = this.doc.getNumberOfPages()
    const { pageSize } = this.doc.internal
    const footerY = pageSize.height - 10

    this.doc.setFont('calibri', 'normal')
    this.doc.setFontSize(9)
    this.doc.setTextColor(120, 120, 120)

    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i)
      this.doc.text(`Page ${i} of ${pageCount}`, pageSize.width - 15, footerY, { align: 'right' })
      this.doc.text('Generated by Inventory System', 15, footerY)
    }
  }

  // --- save + public ---------------------------------------------------------

  __save(storeName = 'store') {
    const stamp = new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14)
    const name = `${storeName}_receipt_summary_${stamp}.pdf`
    this.doc.save(name)
  }

  /**
   * @param {HeaderData} headerData
   * @param {Row[]} rows
   */
  generate(headerData, rows = []) {
    const { storeName, dateRange, generatedAt } = headerData
    this.__header({ storeName, dateRange, generatedAt })

    this.__meta(headerData)

    this.__table(rows)
    this.__pageFooter()
    this.__save(storeName)
  }
}

/**
 * @typedef {[string, {content: string; styles: {textColor: string; fontStyle: import('jspdf-autotable').FontStyle}}, number, string, string, string]} Row
 * 
 * @typedef {Object} BrandType
 * @property {string} name
 * @property {number} amount
 * 
 * @typedef {Object} HeaderData
 * @property {string} storeName
 * @property {string} dateRange
 * @property {string} generatedBy
 * @property {number} count
 * @property {number} totalAmount
 * @property {number} totalDiscount
 * @property {string|Date} generatedAt
 * @property {string|Date|null} generatedBy
 * @property {Record<string, BrandType>} brandTypes
 */
